import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, ShieldAlert, AlertTriangle, Check, ArrowRight, EyeOff } from "lucide-react";
import { portalApi } from "@/lib/portalApi";
import { TraitBar } from "@/components/portal/Primitives";
import { BIG_FIVE_TRAITS, DARK_TRIAD_TRAITS, TRAIT_LABELS, BEHAVIOR_LABELS, SEVERITY_STYLES } from "@/data/questionnaires";

export default function SharedReportPage() {
  const { token } = useParams();
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    portalApi
      .sharedReport(token)
      .then(setReport)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [token]);

  return (
    <div className="min-h-screen bg-[#020617]">
      <div
        className="pointer-events-none fixed inset-0 opacity-[0.35]"
        style={{
          background:
            "radial-gradient(60rem 40rem at 85% -10%, rgba(212,175,55,0.10), transparent 60%), radial-gradient(50rem 30rem at 5% 10%, rgba(56,89,163,0.14), transparent 65%)",
        }}
      />
      <header className="relative border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="font-serif text-lg text-white" data-testid="shared-home-link">
            Bad Boss <span className="text-[#D4AF37]">Clinic</span>
          </Link>
          <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500 flex items-center gap-2">
            <EyeOff size={12} /> Shared · identity removed
          </span>
        </div>
      </header>

      <main className="relative max-w-4xl mx-auto px-6 py-12">
        {loading && (
          <div className="flex justify-center py-24" data-testid="shared-loading">
            <Loader2 className="animate-spin text-[#D4AF37]" size={26} />
          </div>
        )}

        {error && (
          <div className="max-w-lg" data-testid="shared-error">
            <ShieldAlert className="text-slate-600 mb-5" size={30} />
            <h1 className="font-serif text-3xl text-white mb-4">This link is no longer available</h1>
            <p className="text-slate-400 text-sm leading-relaxed mb-8">{error}</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] px-6 py-3 text-xs uppercase tracking-[0.2em] hover:bg-[#D4AF37] hover:text-slate-950 transition-colors"
            >
              Visit Bad Boss Clinic <ArrowRight size={14} />
            </Link>
          </div>
        )}

        {report && (
          <div data-testid="shared-report">
            <p className="text-[#D4AF37] text-xs uppercase tracking-[0.28em] mb-3">Shared analysis</p>
            <h1 className="font-serif text-3xl md:text-4xl text-white mb-4">Vulnerability report</h1>
            <p className="text-slate-400 text-sm leading-relaxed max-w-2xl mb-2">
              A colleague shared this with you. It pairs their personality profile against their leader's dark triad
              profile to show which trait interactions can be exploited.
            </p>
            <p className="text-slate-600 text-xs mb-10">
              Generated {new Date(report.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })} ·
              based on {report.incident_count} logged {report.incident_count === 1 ? "incident" : "incidents"} ·
              names, contact details and the raw incident log are not included
            </p>

            <div className="grid md:grid-cols-2 gap-5 mb-10">
              <div className="border border-slate-800 bg-[#0B1220]/80 p-6 md:p-8" data-testid="shared-big-five">
                <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500 mb-6">Their profile</p>
                <div className="space-y-5">
                  {BIG_FIVE_TRAITS.map((t) => (
                    <TraitBar key={t} label={TRAIT_LABELS[t]} value={report.big_five_scores?.[t] ?? 0} />
                  ))}
                </div>
              </div>
              <div className="border border-slate-800 bg-[#0B1220]/80 p-6 md:p-8" data-testid="shared-dark-triad">
                <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500 mb-6">Their leader's profile</p>
                <div className="space-y-5">
                  {DARK_TRIAD_TRAITS.map((t) => (
                    <TraitBar
                      key={t}
                      label={TRAIT_LABELS[t]}
                      value={report.dark_triad_scores?.[t] ?? 0}
                      color={(report.dark_triad_scores?.[t] ?? 0) >= 60 ? "#F87171" : "#FB923C"}
                    />
                  ))}
                </div>
              </div>
            </div>

            {report.escalation_reason && (
              <div className="border border-red-500/30 bg-red-500/[0.06] p-6 md:p-8 mb-10" data-testid="shared-escalation">
                <div className="flex items-center gap-2.5 mb-4">
                  <AlertTriangle size={15} className="text-red-400" />
                  <p className="text-[11px] uppercase tracking-[0.24em] text-red-400">Escalation risk</p>
                </div>
                <p className="text-slate-300 text-sm leading-relaxed">{report.escalation_reason}</p>
              </div>
            )}

            <h2 className="font-serif text-2xl text-white mb-6">
              {report.vulnerabilities.length} exploitable trait interactions
            </h2>
            <div className="space-y-5 mb-14">
              {report.vulnerabilities.map((v, idx) => {
                const style = SEVERITY_STYLES[v.severity] || SEVERITY_STYLES.moderate;
                return (
                  <div
                    key={v.id || idx}
                    className="border bg-[#0B1220]/80 p-6 md:p-8"
                    style={{ borderColor: style.border, animation: `fadeInUp 0.4s ease-out ${idx * 80}ms both` }}
                    data-testid={`shared-vulnerability-${idx}`}
                  >
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                      <span
                        className="text-[10px] uppercase tracking-[0.2em] px-2.5 py-1"
                        style={{ color: style.color, backgroundColor: style.bg, border: `1px solid ${style.border}` }}
                      >
                        {style.label}
                      </span>
                      <span className="font-mono text-[11px] text-slate-500">{v.trait_interaction}</span>
                    </div>
                    <h3 className="font-serif text-xl md:text-2xl text-white mb-4">{v.label}</h3>
                    <p className="text-slate-400 text-sm leading-relaxed mb-5">{v.description}</p>
                    {v.linked_behaviors?.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {v.linked_behaviors.map((b) => (
                          <span key={b} className="text-[10px] uppercase tracking-widest border border-slate-800 text-slate-500 px-2 py-1">
                            {BEHAVIOR_LABELS[b] || b}
                          </span>
                        ))}
                      </div>
                    )}
                    {v.counter_strategies?.length > 0 && (
                      <div className="border-t border-slate-800 pt-5 mt-5">
                        <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500 mb-4">Counter-strategies</p>
                        <ul className="space-y-3">
                          {v.counter_strategies.map((s) => (
                            <li key={s} className="flex gap-3 text-slate-300 text-sm leading-relaxed">
                              <Check size={14} className="text-[#D4AF37] shrink-0 mt-1" />
                              {s}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {report.dataset_match && (
              <div className="border border-[#D4AF37]/25 bg-[#0B1220]/80 mb-14" data-testid="shared-cohort-match">
                <div className="p-6 md:p-8 border-b border-slate-800">
                  <p className="text-[11px] uppercase tracking-[0.24em] text-[#D4AF37] mb-3">BADBOSS survey match</p>
                  <h3 className="font-serif text-2xl md:text-3xl text-white mb-3">
                    {report.dataset_match.closest_match_percent
                      ? `${report.dataset_match.closest_match_percent}% match`
                      : "Cohort matched"}{" "}
                    <span className="text-slate-500 text-base">to the closest documented case</span>
                  </h3>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    Matched against {(report.dataset_match.dataset_size || 0).toLocaleString()} anonymised cases from the
                    clinic's global survey. Nearest {report.dataset_match.cohort_size} cases span{" "}
                    {report.dataset_match.cohort_profile?.countries || 0} countries.
                  </p>
                </div>
                {(report.dataset_match.cohort_profile?.top_behaviours?.length || 0) > 0 && (
                  <div className="p-6 md:p-8 border-b border-slate-800">
                    <p className="text-[10px] uppercase tracking-[0.24em] text-slate-500 mb-4">Most common behaviours in this cohort</p>
                    <div className="space-y-3">
                      {report.dataset_match.cohort_profile.top_behaviours.map((b) => (
                        <div key={b.dimension} className="flex items-center gap-4">
                          <span className="text-slate-300 text-sm flex-1">{b.label}</span>
                          <div className="w-24 h-1.5 bg-slate-800">
                            <div className="h-full bg-[#D4AF37]" style={{ width: `${Math.min(100, b.share)}%` }} />
                          </div>
                          <span className="font-mono text-xs text-slate-400 w-10 text-right">{b.share}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {report.dataset_match.cohort_summary && (
                  <div className="p-6 md:p-8">
                    <p className="text-slate-300 text-sm leading-relaxed">{report.dataset_match.cohort_summary}</p>
                  </div>
                )}
              </div>
            )}

            <div className="border border-[#D4AF37]/40 bg-gradient-to-br from-[#D4AF37]/[0.08] to-transparent p-7 md:p-10" data-testid="shared-cta">
              <h3 className="font-serif text-2xl text-white mb-3">Recognise any of this at your own work?</h3>
              <p className="text-slate-400 text-sm max-w-xl leading-relaxed mb-7">
                Run the same two assessments on yourself and your leader — free, and it takes about ten minutes.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to={report.referral_code ? `/portal/register?ref=${report.referral_code}` : "/portal/register"}
                  data-testid="shared-register-btn"
                  className="bg-[#D4AF37] text-slate-950 px-7 py-3.5 text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#B5952F] transition-colors"
                >
                  Create a free account
                </Link>
                <Link
                  to="/"
                  className="border border-slate-700 text-slate-300 px-7 py-3.5 text-xs uppercase tracking-[0.2em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
                >
                  About the Clinic
                </Link>
              </div>
            </div>
          </div>
        )}
      </main>

      <footer className="relative max-w-4xl mx-auto px-6 pb-10">
        <p className="text-slate-600 text-xs border-t border-slate-900 pt-6" data-testid="shared-entity-line">
          Bad Boss Clinic is a service of Apex Advisory.
        </p>
      </footer>
    </div>
  );
}
